import { Button, Dialog, DialogActions, DialogContent, FormControl, FormHelperText, MenuItem, Select, TextField } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SiteCommissioningItem, SiteCommissioningSummary } from "../../../../../dtos/siteCommissioning";
import { FormField } from "../../../../shared/formField";
import { FormSection } from "../../../../shared/formSection";
import { ValidationErrors } from "../../../../shared/validationErrors";
import { useApi } from "../../../../useApi";

import { isIntegerInRange } from "./validation";
import { DIALOG_LABEL_WIDTH } from "./dialogLayout";
import { removeSensor, splitTopologyItems } from "./siteTopologyEditing";
import { formatIoDeviceKey } from "./ioDeviceLabels";

const DMP_ZONE_NUMBER_MIN = 500;
const DMP_ZONE_NUMBER_MAX = 999;
const DMP_SERIAL_NUMBER_LENGTH = 8;
const DMP_SENSOR_NAME_MAX_LENGTH = 32;
const DMP_SENSOR_NAME_REGEX = /^[a-zA-Z0-9\s]+$/;

interface Props {
    open: boolean;
    onClose: () => void;
    siteId: number;
    existingItems: Record<string, SiteCommissioningItem>;
    ioDeviceKeys: string[];
    editingKey?: string;
    onSaved: (summary: SiteCommissioningSummary) => void;
}

function parseSensorKey(key: string): { ioDeviceKey: string; serialNumber: string } {
    const withoutPrefix = key.substring("sensor:".length);
    const lastColon = withoutPrefix.lastIndexOf(":");
    return { ioDeviceKey: withoutPrefix.substring(0, lastColon), serialNumber: withoutPrefix.substring(lastColon + 1) };
}

function isDmpDeviceKey(key: string): boolean {
    return key.startsWith("ioDevice:dmp:");
}

function nextDmpZoneNumber(existingItems: Record<string, SiteCommissioningItem>): string {
    const usedZones = Object.entries(existingItems)
        .filter(([key]) => key.startsWith("sensor:"))
        .map(([, item]) => Number(item.fields.zoneNumber))
        .filter((zone) => Number.isInteger(zone));
    const next = usedZones.length > 0 ? Math.max(...usedZones) + 1 : DMP_ZONE_NUMBER_MIN;
    return next <= DMP_ZONE_NUMBER_MAX ? String(next) : "";
}

export const EditSensorDialog: React.FC<Props> = ({ open, onClose, siteId, existingItems, ioDeviceKeys, editingKey, onSaved }) => {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [ioDeviceKey, setIoDeviceKey] = useState("");
    const [serialNumber, setSerialNumber] = useState("");
    const [name, setName] = useState("");
    const [zoneNumber, setZoneNumber] = useState("");
    const [invertSerialNumber, setInvertSerialNumber] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    const isEditing = Boolean(editingKey);
    const isDmpDevice = isDmpDeviceKey(ioDeviceKey);

    useEffect(() => {
        if (open) {
            if (editingKey) {
                const parsed = parseSensorKey(editingKey);
                const fields = existingItems[editingKey]?.fields;
                setIoDeviceKey(parsed.ioDeviceKey);
                setSerialNumber(parsed.serialNumber);
                setName(typeof fields?.name === "string" ? fields.name : "");
                setZoneNumber(fields?.zoneNumber !== undefined && fields?.zoneNumber !== null ? String(fields.zoneNumber) : "");
                setInvertSerialNumber(fields?.invertSerialNumber === true);
            } else {
                const initialDeviceKey = ioDeviceKeys[0] ?? "";
                setIoDeviceKey(initialDeviceKey);
                setSerialNumber("");
                setName("");
                setZoneNumber(isDmpDeviceKey(initialDeviceKey) ? nextDmpZoneNumber(existingItems) : "");
                setInvertSerialNumber(false);
            }
            setErrors([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editingKey]);

    const save = () => {
        const trimmedSerial = serialNumber.trim();
        const trimmedName = name.trim();
        const clientErrors: string[] = [];
        if (!ioDeviceKey) {
            clientErrors.push("Select the I/O device this sensor is attached to.");
        }
        if (!trimmedSerial) {
            clientErrors.push("Serial number is required.");
        } else if (isDmpDevice && trimmedSerial.length !== DMP_SERIAL_NUMBER_LENGTH) {
            clientErrors.push(`DMP sensor serial number must be exactly ${DMP_SERIAL_NUMBER_LENGTH} characters.`);
        }
        if (!trimmedName) {
            clientErrors.push("Sensor name is required.");
        } else if (isDmpDevice && trimmedName.length > DMP_SENSOR_NAME_MAX_LENGTH) {
            clientErrors.push(`Sensor name must be ${DMP_SENSOR_NAME_MAX_LENGTH} characters or fewer.`);
        } else if (isDmpDevice && !DMP_SENSOR_NAME_REGEX.test(trimmedName)) {
            clientErrors.push("Sensor name must contain only letters, numbers, and spaces.");
        }
        if (isDmpDevice) {
            if (!zoneNumber) {
                clientErrors.push("Zone number is required for DMP sensors.");
            } else if (!isIntegerInRange(zoneNumber, DMP_ZONE_NUMBER_MIN, DMP_ZONE_NUMBER_MAX)) {
                clientErrors.push(`Zone number must be between ${DMP_ZONE_NUMBER_MIN} and ${DMP_ZONE_NUMBER_MAX}.`);
            } else if (Object.entries(existingItems).some(([key, item]) =>
                key.startsWith("sensor:") && key !== editingKey && Number(item.fields.zoneNumber) === Number(zoneNumber)
            )) {
                clientErrors.push(`Zone number ${zoneNumber} is already used by another sensor.`);
            }
        }
        if (clientErrors.length > 0) {
            setErrors(clientErrors);
            return;
        }

        const newKey = `sensor:${ioDeviceKey}:${trimmedSerial}`;
        let items = editingKey && editingKey !== newKey ? removeSensor(existingItems, editingKey) : { ...existingItems };

        if ((!editingKey || editingKey !== newKey) && items[newKey]) {
            setErrors([`A sensor with serial number ${trimmedSerial} already exists on this device.`]);
            return;
        }

        const fields: Record<string, unknown> = { name: trimmedName };
        if (isDmpDevice && zoneNumber) {
            fields.zoneNumber = Number(zoneNumber);
        } else if (!isDmpDevice) {
            fields.invertSerialNumber = invertSerialNumber;
        }

        items = { ...items, [newKey]: { fields } };

        setSaving(true);
        const { ioDeviceItems, boxItems } = splitTopologyItems(items);
        siteApi
            .updateCommissioning(siteId, [
                { name: "ioDevices", items: ioDeviceItems },
                { name: "boxes", items: boxItems },
            ])
            .then((r) => {
                setSaving(false);
                if (r.result === AjaxResult.Success && r.data) {
                    onSaved(r.data);
                    onClose();
                    enqueueSnackbar(isEditing ? "Sensor updated" : "Sensor added", { variant: "success" });
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to save the sensor", { variant: "error" });
                }
            });
    };

    return (
        <Dialog open={open} disablePortal>
            <DialogContent style={{ minWidth: "400px" }}>
                <FormSection headerText={isEditing ? "Edit Sensor" : "Add Sensor"}>
                    <FormField labelWidth={DIALOG_LABEL_WIDTH} label="I/O Device" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <Select
                                value={ioDeviceKey}
                                onChange={(event) => {
                                    const selectedKey = event.target.value as string;
                                    setIoDeviceKey(selectedKey);
                                    if (isDmpDeviceKey(selectedKey) && !zoneNumber) {
                                        setZoneNumber(nextDmpZoneNumber(existingItems));
                                    }
                                }}
                                variant="outlined"
                                disabled={isEditing}
                            >
                                {ioDeviceKeys.map((key) => (
                                    <MenuItem key={key} value={key}>{formatIoDeviceKey(key)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FormField>
                    <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Name" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={name}
                                onChange={(event) => setName(event.currentTarget.value)}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Serial Number" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={serialNumber}
                                onChange={(event) => setSerialNumber(event.currentTarget.value)}
                                variant="outlined"
                                disabled={isEditing}
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    {isDmpDevice && (
                        <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Zone Number" required>
                            <FormControl style={{ flexGrow: 1 }}>
                                <TextField
                                    value={zoneNumber}
                                    onChange={(event) => setZoneNumber(event.currentTarget.value)}
                                    variant="outlined"
                                    InputProps={{ inputProps: { style: { padding: "8px" } } }}
                                />
                            </FormControl>
                        </FormField>
                    )}
                    {!isDmpDevice && (
                        <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Sensor Type" required>
                            <FormControl style={{ flexGrow: 1 }}>
                                <Select
                                    value={invertSerialNumber ? "other" : "paznic"}
                                    onChange={(event) => setInvertSerialNumber(event.target.value === "other")}
                                    variant="outlined"
                                >
                                    <MenuItem value="paznic">Paznic</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                                <FormHelperText>Other sensors reverse the serial number order; Paznic sensors do not.</FormHelperText>
                            </FormControl>
                        </FormField>
                    )}
                    <ValidationErrors errors={errors} />
                </FormSection>
            </DialogContent>
            <DialogActions>
                <Button color="primary" variant="contained" onClick={save} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button color="secondary" variant="contained" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
